import { useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import intersection from 'lodash.intersection'
import uniq from 'lodash.uniq'

import { selectIsUserAuthenticated, selectUserCurrencyIsARS } from 'features/auth/selectors'
import { selectCategoriesGames, selectFavouriteList, selectTopSlots } from 'features/slots/selectors'

import { KEVINS_PICKS_CATEGORY, getKevinsPicksOrder, insertKevinsPicks, selectKevinsPicks } from '../utils/kevinsPicks'
import { tabs } from '../data/tabs'
import { tabsLive } from '../data/tabsLive'
import { excludeSlotsForARS } from '../providersData/GameCatalog/GameCatalogUtils'
import { getPureGameId } from '../providersData/GameCatalog/uniqGameId'
import { Providers } from '../types/configuration'
import { IFiltersSlots } from '../types/filters'
import { ISlotsGameType, ISlotViewItem, SlotsGameType } from '../types/interfaces/Slots/Slots'
import { HALLOWEEN_CATEGORY, halloweenGames, getHalloweenMatchId } from '../utils/halloween'
import {
  amaticProviders,
  categoriesSlots,
  categoriesSlotsFreeSpin,
  categoriesSlotsLive,
  categoriesSortArray,
  excludeProvidersCasino,
  liveCasinoProviders,
  liveCasinoProvidersSortArray,
  providersAuthReq,
  providersSortArray,
  starCasinoProviders,
  virtualProviders,
} from '../utils/slots'
import { additionalTopSlotsIds } from '../data/additionalTopSlots'

export interface IUsefilterSlotsParameters {
  filters: IFiltersSlots
  slots: ISlotViewItem[]
  slotsCount?: number
  gameType?: ISlotsGameType
}

const useFilterSlots = ({ filters, slots, slotsCount = slots.length, gameType }: IUsefilterSlotsParameters) => {
  const { selectedCategories: sCategories, selectedProviders, searchString = '', selectedSort } = filters
  const selectedCategories = useMemo(() => sCategories.filter(t => t !== 'all'), [sCategories])
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const favoriteSlots = useSelector(selectFavouriteList, shallowEqual)
  const categoriesGames = useSelector(selectCategoriesGames, shallowEqual)
  const slotsSortArray = useSelector(selectTopSlots, shallowEqual)
  const userCurrencyIsARS = useSelector(selectUserCurrencyIsARS)

  const baseSlotsByType = useMemo(() => {
    switch (gameType) {
      case SlotsGameType.casino:
        return slots.filter(game => {
          if (game.gameType) {
            return game.gameType === gameType
          }

          return excludeProvidersCasino.indexOf(game.provider) === -1
        })
      case SlotsGameType.liveCasino:
        return slots.filter(game => {
          if (game.gameType) {
            return game.gameType === gameType
          }

          return liveCasinoProviders.includes(game.provider)
        })
      case SlotsGameType.freeSpin:
        return slots.filter(game => game.hasFreeSpins)
      case SlotsGameType.amatic:
        return slots.filter(game => amaticProviders.includes(game.provider) && game.routeProvider === 'otc')
      case SlotsGameType.starCasino:
        return slots.filter(game => starCasinoProviders.includes(game.provider))
      case SlotsGameType.virtual:
        return slots.filter(game => virtualProviders.includes(game.provider))
      default:
        return slots
    }
  }, [slots, gameType])

  const slotsByType = useMemo(
    () =>
      gameType === 'live-casino'
        ? baseSlotsByType.map(slot =>
            getKevinsPicksOrder(slot) < 0
              ? slot
              : {
                  ...slot,
                  tab: [...slot.tab, KEVINS_PICKS_CATEGORY],
                  tabLive: [...slot.tabLive, KEVINS_PICKS_CATEGORY],
                },
          )
        : baseSlotsByType,
    [baseSlotsByType, gameType],
  )

  const tabByGameType = useMemo(
    () => (slot: ISlotViewItem) => {
      switch (gameType) {
        case SlotsGameType.casino:
          return slot.tab
        case SlotsGameType.freeSpin:
          return slot.tab
        default:
          return slot.tabLive
      }
    },
    [gameType],
  )

  const tabNamesByGameType = useMemo(() => {
    switch (gameType) {
      case SlotsGameType.casino:
        return tabs
      case SlotsGameType.freeSpin:
        return tabs
      default:
        return tabsLive
    }
  }, [gameType])

  const providersSortArrayByGameType = useMemo(() => {
    if (gameType === SlotsGameType.liveCasino) {
      return liveCasinoProvidersSortArray
    }
    return providersSortArray
  }, [gameType])

  const filteredSlots: ISlotViewItem[] = useMemo(
    () =>
      slotsByType
        .filter(slot => {
          const slotTab = tabByGameType(slot)
          const matchesDynamicCategory = selectedCategories.some(ctg =>
            categoriesGames?.[ctg]?.games?.includes(slot.gameId),
          )
          return (
            (slot.gameName.toUpperCase().indexOf(searchString.toUpperCase()) !== -1 || !searchString) &&
            (!(providersAuthReq.indexOf(slot.provider) !== -1) || isAuth) &&
            (selectedProviders.indexOf(slot.provider.toLowerCase()) !== -1 || !selectedProviders.length) &&
            (intersection(selectedCategories, slotTab).length ||
              !selectedCategories.length ||
              selectedCategories.indexOf('favorite') !== -1 ||
              matchesDynamicCategory)
          )
        })
        .filter(slot => {
          if (!userCurrencyIsARS) return true

          const pureId = getPureGameId(slot.gameId)
          return !excludeSlotsForARS.includes(pureId)
        })
        .sort(
          (a, b) =>
            (providersSortArrayByGameType.indexOf(a.provider) + 1 || Infinity) -
            (providersSortArrayByGameType.indexOf(b.provider) + 1 || Infinity - 1),
        )
        .sort((a, b) => (a.routeProvider === 'otc' ? -1 : 1)) // outcome to the top
        .sort((a, b) => {
          const sortByCategory = (
            tabNamesByGameType[selectedCategories[0] as keyof typeof tabNamesByGameType] || [
              ...additionalTopSlotsIds,
              ...slotsSortArray,
            ]
          ).map(getPureGameId)

          return (
            (sortByCategory.indexOf(getPureGameId(a.gameId)) + 1 || Infinity) -
            (sortByCategory.indexOf(getPureGameId(b.gameId)) + 1 || Infinity - 1)
          )
        })
        .sort((a, b) => (selectedSort === 'a-z' ? a.gameName.localeCompare(b.gameName) : 0)),
    [
      selectedCategories,
      slotsByType,
      selectedProviders,
      isAuth,
      searchString,
      tabByGameType,
      tabNamesByGameType,
      slotsSortArray,
      providersSortArrayByGameType,
      selectedSort,
      categoriesGames,
    ],
  )

  const halloweenOrderedSlots: ISlotViewItem[] = useMemo(() => {
    if (!selectedCategories.includes(HALLOWEEN_CATEGORY)) return filteredSlots
    const order = halloweenGames.map(getHalloweenMatchId)
    const seen = new Set<string>()
    return filteredSlots
      .filter(slot => {
        const matchId = getHalloweenMatchId(slot.gameId)
        if (seen.has(matchId)) return false
        seen.add(matchId)
        return true
      })
      .sort((a, b) => {
        const left = order.indexOf(getHalloweenMatchId(a.gameId))
        const right = order.indexOf(getHalloweenMatchId(b.gameId))
        if (left === -1 && right === -1) return 0
        if (left === -1) return 1
        if (right === -1) return -1
        return left - right
      })
  }, [filteredSlots, selectedCategories])

  const providers = useMemo(
    () =>
      uniq(
        slotsByType
          .filter(temp => {
            const matchesDynamicCategory = selectedCategories.some(ctg =>
              categoriesGames?.[ctg]?.games?.includes(temp.gameId),
            )
            return (
              intersection(selectedCategories, temp.tab).length || !selectedCategories.length || matchesDynamicCategory
            )
          })
          .map(t => t.provider),
      )
        .concat(isAuth ? [] : providersAuthReq)
        .sort(
          (a: string, b: string) =>
            (providersSortArrayByGameType.indexOf(a.toLowerCase()) + 1 || Infinity - 1) -
            (providersSortArrayByGameType.indexOf(b.toLowerCase()) + 1 || Infinity),
        ),
    [slotsByType, selectedCategories, isAuth, providersSortArrayByGameType, categoriesGames],
  )

  // борьба с оптимизаией
  const categories = useMemo(() => {
    const withoutHalloween = categoriesSlots.filter(category => category !== HALLOWEEN_CATEGORY)
    const casinoCategories = categoriesSlots

    switch (gameType) {
      case SlotsGameType.casino:
        return casinoCategories
      case SlotsGameType.freeSpin:
        return categoriesSlotsFreeSpin
      case SlotsGameType.liveCasino:
        return categoriesSlotsLive
      default:
        return withoutHalloween
    }
  }, [gameType])

  const resultProviders = useMemo(() => ['all', ...providers], [providers])

  return {
    favoriteSlots,
    slots: selectedCategories.includes(KEVINS_PICKS_CATEGORY)
      ? selectKevinsPicks(halloweenOrderedSlots)
      : halloweenOrderedSlots,
    providers: resultProviders,
    categories: gameType === 'live-casino' ? insertKevinsPicks(categories, 'popular') : categories,
  }
}

export default useFilterSlots
